import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useCredits from 'app/hooks/useCredits';
import useFeatureFlag from 'app/hooks/useFeatureFlag';
import useFeatureFlagWithContext from 'app/hooks/useFeatureFlagWithContext';
import useUserFeatureFlag from 'app/hooks/useUserFeatureFlag';
import { FEATURE_FLAGS } from 'app/constants/FeatureFlag';
import { usePlanExploration } from 'app/hooks/usePlanExploration';
import { useJusGptV2Access } from 'app/services/jusGptAccess';
import {
  MAIN_MENU_ITEMS,
  SETTINGS_MENU_ITEMS,
  MENU_DRAWER_CONFIG,
  DrawerType,
  MenuItemId,
  DrawerItemId,
  type SidebarBadgeConfig,
  type ResolvedMenuItem,
  type ResolvedMenuDrawerConfig,
  type ResolvedDrawerItem,
  type ResolvedDrawerSection,
  type DrawerItemInput,
  type MenuDrawerConfig,
  SidebarBadgeType,
} from '../MenuConfig';
import { config } from 'config/env';
import { ROUTES_PATH } from 'app/constants/Routes';
import type { RootState } from 'redux/types';
import { useBulkQueriesHability } from './useBulkQueriesHability';
import { useJusZapMenuBadge } from './useJusZapMenuBadge';
import { useRealtimeNotificationsInbox } from 'app/modules/RealtimeNotificationsProvider';
import type { ProfileMenuAccountItem } from '../components/ProfileMenu';

const SUBUSER_DISABLED_TOOLTIP =
  'Esta funcionalidade não está disponível em seu plano';

const resolveNewTagBadge = (
  noveltyDate?: Date,
): SidebarBadgeConfig | undefined => {
  if (!noveltyDate) return undefined;
  const now = new Date();
  const endMs =
    noveltyDate.getTime() +
    config.sidebar.newTagDurationDays * 24 * 60 * 60 * 1000;
  if (now < noveltyDate || now.getTime() >= endMs) return undefined;

  return { kind: SidebarBadgeType.NEW_TAG } as const;
};

const resolveMainLevelNewTagBadge = (
  item: { isNew?: boolean; id?: MenuItemId; noveltyDate?: Date },
  isDriveEnabled = false,
): SidebarBadgeConfig | undefined => {
  if (item.isNew !== true) return undefined;
  // Enquanto o Drive não está ligado, a Jurisprudência mantém o "Novo".
  // Quando o Drive liga, o destaque migra para o Meu Drive.
  if (item.id === MenuItemId.JURISPRUDENCIA && isDriveEnabled) return undefined;
  if (item.noveltyDate && !resolveNewTagBadge(item.noveltyDate)) {
    return undefined;
  }

  return {
    kind: SidebarBadgeType.NEW_TAG,
    shimmer:
      item.id === MenuItemId.JURISPRUDENCIA ||
      item.id === MenuItemId.MEU_DRIVE,
  };
};

const resolveDrawerLevelNewTagBadge = (
  item: { isNew?: boolean; noveltyDate?: Date },
): SidebarBadgeConfig | undefined => {
  if (!item.isNew || !item.noveltyDate) {
    return undefined;
  }

  return resolveNewTagBadge(item.noveltyDate);
};

const resolveNotificationTagBadge = (
  pathKey: string | undefined,
  unreadCountByPath: Record<string, number>,
): SidebarBadgeConfig | undefined => {
  if (!pathKey) {
    return undefined;
  }

  const count = unreadCountByPath[pathKey] ?? 0;
  if (count <= 0) {
    return undefined;
  }

  return {
    kind: SidebarBadgeType.NOTIFICATION_TAG,
    count,
  };
};

const resolveDrawerBadge = (
  drawerType?: DrawerType | null,
  context?: {
    isRestricted: boolean;
    isLoading: boolean;
    enableSubuserCreation: boolean;
    canUseJusagenda: boolean;
    enabledBatchQueries: boolean;
    isSecurityPageEnabled: boolean;
    isInstagramCreatorEnabled: boolean;
    isJusScoreRhEnabled: boolean;
  },
  unreadCountByPath?: Record<string, number>,
): SidebarBadgeConfig | undefined => {
  if (!drawerType || !context) return undefined;
  const pathCounts = unreadCountByPath ?? {};
  const sections = (MENU_DRAWER_CONFIG as MenuDrawerConfig)[drawerType] ?? [];

  let notificationCount = 0;

  for (const section of sections) {
    for (const item of section.items) {
      const resolvedItem = resolveDrawerItem(item, context);
      if (!resolvedItem.show) continue;

      const effectiveBadge = resolvedItem.hideBadge
        ? resolvedItem.badge
        : (resolveNotificationTagBadge(resolvedItem.path, pathCounts) ??
          resolvedItem.badge);

      if (!effectiveBadge) continue;

      if (effectiveBadge.kind === SidebarBadgeType.NOTIFICATION_TAG) {
        notificationCount += Math.max(0, effectiveBadge.count);
      }
    }
  }

  if (notificationCount > 0) {
    return {
      kind: SidebarBadgeType.NOTIFICATION_TAG,
      count: notificationCount,
    };
  }

  return undefined;
};

function resolveMenuItem(
  item: ResolvedMenuItem,
  context: {
    isRestricted: boolean;
    isLoading: boolean;
    badge?: SidebarBadgeConfig;
  },
): ResolvedMenuItem {
  const globalDisabled = context.isRestricted && !context.isLoading;
  const alwaysEnabledForRegularization =
    item.id === MenuItemId.CONFIGURACOES ||
    item.id === MenuItemId.PERFIL ||
    item.id === MenuItemId.GAMIFICACAO;

  return {
    ...item,
    show: true,
    disabled: alwaysEnabledForRegularization ? false : globalDisabled,
    disabledTooltip: undefined,
    badge: context.badge,
  };
}

function resolveDrawerItem(
  item: DrawerItemInput,
  context: {
    isRestricted: boolean;
    isLoading: boolean;
    enableSubuserCreation: boolean;
    canUseJusagenda: boolean;
    enabledBatchQueries: boolean;
    isSecurityPageEnabled: boolean;
    isInstagramCreatorEnabled: boolean;
    isJusScoreRhEnabled: boolean;
  },
): ResolvedDrawerItem {
  const globalDisabled = context.isRestricted && !context.isLoading;
  const alwaysEnabledForRegularization =
    item.id === DrawerItemId.PLANO_PAGAMENTO ||
    item.id === DrawerItemId.PERFIL ||
    item.id === DrawerItemId.SEGURANCA;
  let disabled = alwaysEnabledForRegularization ? false : globalDisabled;
  let disabledTooltip: string | undefined;
  let show = true;

  if (item.id === DrawerItemId.AGENDA && !context.canUseJusagenda) {
    show = false;
  }

  if (item.id === DrawerItemId.CONSULTAS_LOTE) {
    show = !!context.enabledBatchQueries;
  }

  if (item.id === DrawerItemId.SEGURANCA) {
    show = context.isSecurityPageEnabled;
  }

  if (item.id === DrawerItemId.CRIADOR_CONTEUDO) {
    show = context.isInstagramCreatorEnabled;
  }

  /*
    O JusScore RH usa a MESMA flag que fecha as rotas do backend
    (`JusScoreRh`): com o item visível e a API respondendo 404, a tela abriria
    para dar erro.
  */
  if (item.id === DrawerItemId.JUSCORE_RH) {
    show = context.isJusScoreRhEnabled;
  }

  if (item.id === DrawerItemId.USUARIOS && !context.enableSubuserCreation) {
    disabled = true;
    disabledTooltip = SUBUSER_DISABLED_TOOLTIP;
  }

  const badge = item.hideBadge
    ? undefined
    : (item.badge ?? resolveDrawerLevelNewTagBadge(item));

  return {
    ...item,
    show,
    disabled,
    disabledTooltip,
    badge,
  };
}

export function useResolvedMenu() {
  const { openPlanExploration, planExplorationModal } = usePlanExploration();
  const {
    unreadCountByPath,
    isLoading: isInboxLoading,
    isFetching: isInboxFetching,
    hasInboxData,
  } = useRealtimeNotificationsInbox();
  const { isRestricted, isLoading } = useCredits();
  const subscription = useSelector(
    (state: {
      subscription: { subscription_status?: { info?: { plan?: string } } };
    }) => state.subscription?.subscription_status,
  );
  const plan = subscription?.info?.plan ?? '';
  const mayUseJusagenda = useSelector(
    (state: RootState) => state.auth.user?.may_use_jusagenda ?? false,
  );
  const clientId = useSelector(
    (state: RootState) => state.auth.user?.client_id,
  );
  const isAffiliateUser = useSelector(
    (state: RootState) => state.auth.user?.is_affiliate === true,
  );

  const enabledBatchQueries = useBulkQueriesHability();
  const { isFeatureFlagEnable: enableSubuserCreation } =
    useFeatureFlagWithContext(
      FEATURE_FLAGS.PERMISSION.ENABLE_SUBUSER_CREATION,
      { plan },
    );
  const { isFeatureFlagEnable: isGamificationEnabled } =
    useFeatureFlagWithContext(FEATURE_FLAGS.RELEASE.GAMIFICATION, {
      clientId: clientId ?? '',
    });
  const { isFeatureFlagEnable: isMgmEnabled } =
    useFeatureFlagWithContext(FEATURE_FLAGS.RELEASE.MGM_ENABLED, {
      clientId: clientId ?? '',
    });
  const { isFeatureFlagEnable: isJurisprudenciaEnabled } =
    useFeatureFlagWithContext(FEATURE_FLAGS.RELEASE.JURISPRUDENCIA, {
      clientId: clientId ?? '',
    });
  const { isFeatureFlagEnable: isAffiliatesEnabled } =
    useFeatureFlagWithContext(FEATURE_FLAGS.RELEASE.AFFILIATES, {
      clientId: clientId ?? '',
    });
  const { isFeatureFlagEnable: isDriveEnabled } = useFeatureFlag(
    FEATURE_FLAGS.KILL_SWITCH.ENABLE_DRIVE_FRONT,
  );
  const {
    isFeatureFlagEnable: isNewDashboardEnabled,
    flagResolved: newDashboardFlagResolved,
  } = useFeatureFlag(FEATURE_FLAGS.KILL_SWITCH.ENABLE_NEW_DASHBOARD);
  const { isFeatureFlagEnable: isSecurityPageEnabled } = useFeatureFlag(
    FEATURE_FLAGS.RELEASE.SECURITY_PAGE,
  );
  const { isFeatureFlagEnable: isInstagramCreatorEnabled } = useUserFeatureFlag(
    FEATURE_FLAGS.RELEASE.INSTAGRAM_CREATOR,
  );
  /*
    `useUserFeatureFlag` e não `useFeatureFlag`: a rota do módulo no backend é
    gateada por `hasUserPermission`, que casa por user_id e por grupo. Com o
    `useFeatureFlag` (que para flag de release pergunta `is-enabled` sem
    contexto), uma allowlist de usuário no Unleash esconderia o item do menu
    justamente para quem foi liberado.
  */
  const { isFeatureFlagEnable: isJusScoreRhEnabled } = useUserFeatureFlag(
    FEATURE_FLAGS.RELEASE.JUSCORE_RH,
  );

  // Acesso à plataforma de IA: quem decide é o backend, em
  // GET /api/jusgpt/v2-access. Substituiu a allowlist de clientId que ficava
  // chumbada no front. Enquanto a resposta não chega, fica desligado — o clique
  // vai pro JusGPT, que funciona.
  const { enabled: aiPlatformEnabled } = useJusGptV2Access();

  const [stableUnreadCountByPath, setStableUnreadCountByPath] =
    useState<Record<string, number>>(unreadCountByPath);

  useEffect(() => {
    if (!isInboxLoading && !isInboxFetching && hasInboxData) {
      setStableUnreadCountByPath(unreadCountByPath);
    }
  }, [isInboxLoading, isInboxFetching, hasInboxData, unreadCountByPath]);

  // Badge do JusZap não vem do inbox de notificações: é derivado do estado da
  // configuração (pendência de conexão ou conversas abertas). Soma no mapa por
  // path pra reaproveitar o pipeline de badges existente.
  const juszapMenuBadgeCount = useJusZapMenuBadge();

  const effectiveUnreadCountByPath = useMemo(() => {
    const base =
      isInboxLoading || isInboxFetching || !hasInboxData
        ? stableUnreadCountByPath
        : unreadCountByPath;

    if (juszapMenuBadgeCount <= 0) return base;

    const juszapPath = ROUTES_PATH.JUSZAP.LANDING_PAGE;
    return {
      ...base,
      [juszapPath]: (base[juszapPath] ?? 0) + juszapMenuBadgeCount,
    };
  }, [
    isInboxLoading,
    isInboxFetching,
    hasInboxData,
    stableUnreadCountByPath,
    unreadCountByPath,
    juszapMenuBadgeCount,
  ]);

  const context = useMemo(
    () => ({
      isRestricted,
      isLoading,
      enableSubuserCreation,
      enabledBatchQueries,
      canUseJusagenda: mayUseJusagenda,
      isSecurityPageEnabled,
      isInstagramCreatorEnabled,
      isJusScoreRhEnabled,
    }),
    [
      isRestricted,
      isLoading,
      enableSubuserCreation,
      enabledBatchQueries,
      mayUseJusagenda,
      isSecurityPageEnabled,
      isInstagramCreatorEnabled,
      isJusScoreRhEnabled,
    ],
  );

  const menuItemContext = useMemo(
    () => ({
      isRestricted: context.isRestricted,
      isLoading: context.isLoading,
    }),
    [context.isRestricted, context.isLoading],
  );

  const mainItems: ResolvedMenuItem[] = useMemo(() => {
    return MAIN_MENU_ITEMS.filter(
      (item) =>
        (item.id !== MenuItemId.JURISPRUDENCIA || isJurisprudenciaEnabled) &&
        (item.id !== MenuItemId.MEU_DRIVE || isDriveEnabled) &&
        (item.id !== MenuItemId.AFILIADOS ||
          (isAffiliatesEnabled && isAffiliateUser)),
    ).map((item) => {
      const resolved = resolveMenuItem(
        { ...item, show: true, disabled: false },
        {
          ...menuItemContext,
          badge:
            resolveMainLevelNewTagBadge(item, isDriveEnabled) ??
            resolveDrawerBadge(
              item.drawerType,
              context,
              effectiveUnreadCountByPath,
            ),
        },
      );
      const notificationBadge = resolveNotificationTagBadge(
        resolved.route,
        effectiveUnreadCountByPath,
      );
      return {
        ...resolved,
        badge: notificationBadge ?? resolved.badge,
      };
    });
  }, [
    menuItemContext,
    effectiveUnreadCountByPath,
    context,
    isJurisprudenciaEnabled,
    isDriveEnabled,
    isAffiliatesEnabled,
    isAffiliateUser,
  ]);

  const settingsItems: ResolvedMenuItem[] = useMemo(() => {
    return SETTINGS_MENU_ITEMS.filter(
      (item) =>
        item.id !== MenuItemId.PERFIL &&
        item.id !== MenuItemId.GAMIFICACAO,
    ).map((item) => {
      const resolved = resolveMenuItem(
        { ...item, show: true, disabled: false },
        {
          ...menuItemContext,
          badge:
            resolveMainLevelNewTagBadge(item) ??
            resolveDrawerBadge(
              item.drawerType,
              context,
              effectiveUnreadCountByPath,
            ),
        },
      );
      const notificationBadge = resolveNotificationTagBadge(
        resolved.route,
        effectiveUnreadCountByPath,
      );
      return {
        ...resolved,
        badge: notificationBadge ?? resolved.badge,
      };
    });
  }, [menuItemContext, effectiveUnreadCountByPath, context]);

  const drawerConfig: ResolvedMenuDrawerConfig = useMemo(() => {
    const resolveSections = (
      sections: { title: string | null; items: DrawerItemInput[] }[],
    ): ResolvedDrawerSection[] =>
      sections.map((section) => ({
        title: section.title || '',
        items: section.items
          .map((item) =>
            resolveDrawerItem(item, {
              isRestricted: context.isRestricted,
              isLoading: context.isLoading,
              enableSubuserCreation: context.enableSubuserCreation,
              canUseJusagenda: context.canUseJusagenda,
              enabledBatchQueries: context.enabledBatchQueries,
              isSecurityPageEnabled: context.isSecurityPageEnabled,
              isInstagramCreatorEnabled: context.isInstagramCreatorEnabled,
              isJusScoreRhEnabled: context.isJusScoreRhEnabled,
            }),
          )
          .map((item) => ({
            ...item,
            badge: item.hideBadge
              ? item.badge
              : (resolveNotificationTagBadge(
                  item.path,
                  effectiveUnreadCountByPath,
                ) ?? item.badge),
          }))
          .filter((item) => item.show),
      })).filter((section) => section.items.length > 0);

    return {
      [DrawerType.PROCESSOS]: resolveSections(MENU_DRAWER_CONFIG.processos),
      [DrawerType.CLIENTES]: resolveSections(MENU_DRAWER_CONFIG.clientes),
      [DrawerType.CONFIGURACOES]: resolveSections(
        MENU_DRAWER_CONFIG.configuracoes,
      ),
      [DrawerType.JUSFYPAY]: resolveSections(MENU_DRAWER_CONFIG.jusfypay),
    };
  }, [
    context.isRestricted,
    context.isLoading,
    context.enableSubuserCreation,
    context.canUseJusagenda,
    context.enabledBatchQueries,
    context.isSecurityPageEnabled,
    context.isInstagramCreatorEnabled,
    context.isJusScoreRhEnabled,
    effectiveUnreadCountByPath,
  ]);

  const profileMenuItem = useMemo(() => {
    const foundProfile = SETTINGS_MENU_ITEMS.find(
      (item) => item.id === MenuItemId.PERFIL,
    );
    return foundProfile
      ? {
          ...foundProfile,
          show: true,
          disabled: false,
          disabledTooltip: undefined,
        }
      : null;
  }, []);

  const gamificationMenuItem = useMemo(() => {
    const found = SETTINGS_MENU_ITEMS.find(
      (item) => item.id === MenuItemId.GAMIFICACAO,
    );
    return found
      ? {
          ...found,
          show: isGamificationEnabled,
          disabled: false,
          disabledTooltip: undefined,
        }
      : null;
  }, [isGamificationEnabled]);

  // Menu de perfil unificado (KILL_SWITCH.ENABLE_NEW_DASHBOARD): reaproveita os
  // itens já resolvidos do drawer de Configurações (mesmo show/disabled/tooltip
  // que o drawer atual usa), só tirando "Perfil" — vira o botão "Editar" no
  // topo do popover, então manter os dois seria a mesma porta duas vezes.
  const profileMenuAccountItems: ProfileMenuAccountItem[] = useMemo(() => {
    return drawerConfig[DrawerType.CONFIGURACOES]
      .flatMap((section) => section.items)
      .filter((item) => item.id !== DrawerItemId.PERFIL)
      .map((item) => ({
        id: item.id ?? item.path,
        icon: item.icon,
        label: item.label,
        path: item.path,
        disabled: item.disabled,
        disabledTooltip: item.disabledTooltip,
      }));
  }, [drawerConfig]);

  const showUnifiedProfileMenu =
    newDashboardFlagResolved && isNewDashboardEnabled;

  return {
    mainItems,
    settingsItems,
    drawerConfig,
    profileMenuItem,
    gamificationMenuItem,
    profileMenuAccountItems,
    showUnifiedProfileMenu,
    isMgmEnabled,
    aiPlatformEnabled,
    jurisprudenciaEnabled: isJurisprudenciaEnabled,
    openPlanExploration,
    planExplorationModal,
  };
}
